/**
 * Einstellungen → Verbindungen → „Panel“. (28.09.2026, Ticket 2713)
 *
 * Ein Kunde, der auch im SSI-Panel angelegt ist, verbindet hier sein
 * Panel-Konto. Danach kann er seine Webseite im Gespräch betreuen — mit
 * SEINEM Zugang. Welche Module er hat, schaltet das Panel frei; die Karte
 * zeigt nur, was das Panel meldet.
 *
 * Stufe V2 (28.09.2026): „Mit Panel verbinden“. Der Knopf holt beim Server
 * die Panel-Seite (POST /api/panel/kopplung) und öffnet sie im
 * System-Browser — im Web per Weiterleitung in diesem Tab, in der App im
 * Browser-Blatt (SFSafariViewController). NIE in einem Rahmen oder einer
 * eingebetteten WebView: dort könnte die App das Panel-Passwort mitlesen.
 * Die Rückkehr erledigt der Server; das Ergebnis zeigt diese Karte
 * (lib/panel-kopplung-rueckkehr.ts).
 *
 * Stufe V3 (29.09.2026): Martins Konto nutzt dieselbe Karte. Für ihn zeigt
 * sie zusätzlich, ob er umgestellt hat (ab dann läuft ALLES über diese
 * Verbindung, es gibt keine festen Schlüssel mehr), welche Rechte das Panel
 * erteilt hat — in Worten —, was nicht erteilt wurde und den Weg
 * „Verbindungen → Rechte ändern“. Kunden sehen weiter ihre Karte, Feld für Feld.
 *
 * ⚠️ Der Schlüssel kommt nie in diese Seite: /api/panel/verbindung antwortet
 *    nie mit ihm, und bei der Kopplung sieht ihn niemand.
 * ⚠️ Das Feld „Schlüssel aus dem Panel“ bleibt nur dem Betrieb für Tests
 *    (`darf_eintragen`, Übergabe 2 des Panels, Punkt 4). Der Server prüft
 *    das selbst; die Karte zeigt es nur, wenn er es erlaubt.
 */
import { inAppBrowserDa, oeffneImApp } from "../app-browser.js";
import { getCapacitorPlugin } from "../native-voice.js";
import { angemeldetesKonto, kopplungsMeldung, nimmKopplungsErgebnis, nimmKopplungsKonto } from "../panel-kopplung-rueckkehr.js";
import { verbindungsKarte } from "./verbindungs-karte.js";

/** Ein Recht in Worten (server/panel-rechte.ts, RECHT_TEXT) — nie der technische Name. */
interface PanelRecht {
  recht: string;
  text: string;
  riskant: boolean;
}

interface PanelAnsicht {
  ok?: boolean;
  /** Nur Martins Konto trägt es; ohne `konto` ist es die Kunden-Karte. */
  konto?: "betreiber";
  umgestellt?: boolean;
  umgestellt_seit?: string | null;
  bereiche?: Array<{ id: string; titel: string; rechte: PanelRecht[] }>;
  nicht_erteilt?: PanelRecht[];
  /** Der Schlüssel läuft in weniger als 30 Tagen ab. */
  ablauf_bald?: boolean;
  verbunden?: boolean;
  gilt?: boolean;
  /** false: das Panel ist gerade nicht erreichbar — die Liste ist der letzte bekannte Stand. */
  erreichbar?: boolean;
  name?: string | null;
  module?: Array<{ modul: string; name: string; gueltig_bis: string | null }>;
  webseiten?: Array<{ id: number; domain: string }>;
  gueltig_bis?: string | null;
  hinweis?: string | null;
  darf_eintragen?: boolean;
  panel?: "gesperrt" | "schon_getrennt" | "nicht_gekoppelt" | "stoerung" | null;
  satz?: string;
  error?: string;
}

const PFAD = "/api/panel/verbindung";
const KOPPLUNG = "/api/panel/kopplung";
let zaehler = 0;

/** Die Sätze der Karte für Martins Konto — ein Ort, damit Text und Test dasselbe sagen. */
export const SAETZE_BETREIBER = {
  unter: "Webseiten, Newsletter, Rechnungen und Belege im SSI-Panel · per Zuruf",
  fest:
    "Vespera arbeitet für dich heute mit festen Schlüsseln. Verbinde dein Panel – danach läuft alles (Webseiten, Newsletter, Rechnungen, Belege) über diese eine Verbindung, und die festen Schlüssel werden nicht mehr benutzt.",
  festHinweis:
    "Du wählst im Panel jedes Recht einzeln. Die riskanten – Veröffentlichen, Rechnungen anlegen, versenden, buchen und löschen, Belege buchen – sind nie vorangehakt.",
  getrennt:
    "Dein Panel ist nicht verbunden. Ohne Verbindung hat Vespera für dich keinen Panel-Zugang – Webseiten, Newsletter, Rechnungen und Belege gehen erst wieder, wenn du neu verbindest. Die festen Schlüssel werden nicht mehr benutzt.",
  gilt:
    "Das Panel nimmt die Verbindung nicht mehr an. Ohne Verbindung hat Vespera für dich keinen Panel-Zugang – die festen Schlüssel werden nicht mehr benutzt. Verbinde neu.",
  rechteAendern:
    "Rechte änderst du im Panel unter Verbindungen → Rechte ändern: Wegnehmen wirkt sofort, Dazugeben geht nach frischer Anmeldung – aber nur, was Vespera bei dieser Verbindung angefragt hat.",
  trennen:
    "Danach hat Vespera für dich keinen Panel-Zugang mehr – Webseiten, Newsletter, Rechnungen und Belege gehen dann nicht, bis du neu verbindest. Die festen Schlüssel gibt es nicht mehr. Trotzdem trennen?",
  verbundenDanach: "Ab jetzt läuft alles für dich über diese Verbindung.",
  keinBuchhaltungsrecht:
    "Für Rechnungen und Belege wurde nichts erteilt. Hast du dich im Panel mit deinem eigenen Konto angemeldet? Mit „Neu verbinden“ kannst du es noch einmal versuchen.",
} as const;

/** „ablauf am 31.12.2030“ — der Satz zur Warnung vor dem Ablauf. */
export function satzAblauf(bis: string | null | undefined): string {
  return `Die Verbindung läuft am ${panelDatum(bis)} ab. Verbinde vorher neu – ohne Verbindung hat Vespera für dich keinen Panel-Zugang.`;
}

/** „2030-12-31“ oder „2030-12-31 23:59:59“ → „31.12.2030“; ohne Datum: unbegrenzt. */
export function panelDatum(wert: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(wert ?? "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "unbegrenzt";
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (klasse) e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

async function frage(pfad: string, init?: RequestInit): Promise<{ status: number; daten: PanelAnsicht | null }> {
  const r = await fetch(pfad, { credentials: "same-origin", ...init });
  const daten = (await r.json().catch(() => null)) as PanelAnsicht | null;
  return { status: r.status, daten };
}

interface BrowserHoerer {
  addListener?: (ereignis: string, f: () => void) => Promise<{ remove?: () => unknown }> | { remove?: () => unknown };
}

/**
 * Die Panel-Seite öffnen. Im Web: dieser Tab geht zum Panel und kommt über
 * die Rückkehr-Adresse zurück. In der App: das Browser-Blatt über der App;
 * wird es geschlossen, liest die Karte ihren Stand neu (`danach`).
 *
 * ⚠️ Ohne Browser-Blatt (App-Fassung vor Build 77) geht `location.assign`
 *    in der App an Safari — auch das ist der System-Browser, kein Rahmen.
 */
function oeffnePanel(url: string, danach: () => void): "blatt" | "weiter" {
  if (inAppBrowserDa()) {
    const browser = getCapacitorPlugin<BrowserHoerer>("Browser");
    let griff: { remove?: () => unknown } | undefined;
    let fertig = false;
    const anmeldung = browser?.addListener?.("browserFinished", () => {
      if (fertig) return;
      fertig = true;
      void griff?.remove?.();
      danach();
    });
    void Promise.resolve(anmeldung)
      .then((g) => {
        griff = g ?? undefined;
        if (fertig) void griff?.remove?.();
      })
      .catch(() => undefined);
    void oeffneImApp({ art: "blatt", url });
    return "blatt";
  }
  window.location.assign(url);
  return "weiter";
}

/**
 * Die Stichworte unter dem Titel, solange die Karte zu ist (22.10.2026): was die Verbindung freigibt, nur Namen.
 * Martins Konto: die erteilten Bereiche („Webseiten · Rechnungen · …“); ein Kunde: seine freigeschalteten Module.
 * Ohne Verbindung oder ohne Stand: der Satz, wofür die Karte da ist.
 */
export function panelStichworte(d: PanelAnsicht, ohne: string): string {
  if (!d.verbunden || d.gilt === false) return ohne;
  const namen = d.konto === "betreiber" ? (d.bereiche ?? []).map((b) => b.titel) : (d.module ?? []).map((m) => m.name);
  const sauber = namen.map((n) => n.trim()).filter((n) => n !== "");
  return sauber.length > 0 ? sauber.join(" · ") : ohne;
}

export function createPanelKarte(): HTMLElement {
  const nr = ++zaehler;
  const KUNDEN_UNTER = "Deine Webseite im SSI-Panel · per Zuruf";
  // 22.10.2026 (Martin): dieselbe Karte wie Google und WHOOP — nur zum Aufklappen. Zu ist der Standard.
  const { karte, status, unter, koerper, setzeOffen } = verbindungsKarte({
    titel: "Panel (SSI-Webseiten)",
    unter: KUNDEN_UNTER,
    klasse: "panel-karte",
    aufklappbar: true,
    merken: "panel",
  });
  const inhalt = el("div", "panel-karte-inhalt");
  // Die Meldung steht AUSSERHALB des Körpers: was ein Knopf oder die Rückkehr aus dem Panel sagt, sieht man auch zu.
  const meldung = el("p", "panel-karte-meldung");
  meldung.setAttribute("role", "status");
  meldung.setAttribute("aria-live", "polite");
  koerper?.append(inhalt);
  karte.append(meldung);

  const melde = (text: string, art: "ok" | "warn" | "still" = "still"): void => {
    meldung.textContent = text;
    if (art === "still") delete meldung.dataset.kind;
    else meldung.dataset.kind = art;
  };

  /** „Mit Panel verbinden“ bzw. „Neu verbinden“: der Knopf, der die Kopplung startet. */
  const kopplungsKnopf = (knopfText: string): HTMLButtonElement => {
    const knopf = el("button", "connector-connect", knopfText);
    knopf.type = "button";
    knopf.addEventListener("click", () => {
      knopf.disabled = true;
      knopf.setAttribute("aria-busy", "true");
      melde("Das Panel wird vorbereitet…");
      void frage(KOPPLUNG, { method: "POST" })
        .then(({ daten }) => {
          const url = (daten as { url?: unknown } | null)?.url;
          if (daten?.ok && typeof url === "string" && url.startsWith("https://panel.ssi.at/verbinden/")) {
            const weg = oeffnePanel(url, () => void laden());
            melde(
              weg === "blatt"
                ? "Melde dich im Panel an und bestätige dort. Danach fragt Vespera im selben Fenster einmal nach deiner Anmeldung – dann ist die Verbindung fertig."
                : "Weiter zum Panel…",
            );
            if (weg === "blatt") {
              knopf.disabled = false;
              knopf.removeAttribute("aria-busy");
            }
            return;
          }
          melde(daten?.satz ?? "Das Verbinden hat nicht geklappt. Bitte später noch einmal.", "warn");
          knopf.disabled = false;
          knopf.removeAttribute("aria-busy");
        })
        .catch(() => {
          melde("Keine Verbindung zum Server — bitte noch einmal versuchen.", "warn");
          knopf.disabled = false;
          knopf.removeAttribute("aria-busy");
        });
    });
    return knopf;
  };

  /** Der Kopplungs-Knopf allein in seiner Reihe (nicht verbunden, „gilt nicht mehr“). */
  const verbindenKnopf = (knopfText: string): HTMLElement => {
    const aktionen = el("div", "connector-actions panel-karte-aktionen");
    aktionen.append(kopplungsKnopf(knopfText));
    return aktionen;
  };

  /** Nur für den Betrieb: einen Schlüssel von Hand eintragen (Tests). */
  const betriebsFormular = (): HTMLElement => {
    const kasten = el("details", "panel-karte-betrieb");
    const zusammenfassung = el("summary", "panel-karte-betrieb-titel", "Schlüssel von Hand eintragen (nur Betrieb)");
    const form = el("form", "panel-karte-form");
    form.noValidate = true;
    const feldId = `panel-schluessel-${nr}`;
    const hinweisId = `panel-schluessel-hinweis-${nr}`;
    const label = el("label", "panel-karte-label", "Schlüssel aus dem Panel");
    label.htmlFor = feldId;
    const zeile = el("div", "panel-karte-zeile");
    const feld = el("input", "panel-karte-feld");
    feld.id = feldId;
    feld.type = "password";
    feld.name = "panel-schluessel";
    feld.autocomplete = "off";
    feld.spellcheck = false;
    feld.setAttribute("autocapitalize", "off");
    feld.setAttribute("autocorrect", "off");
    feld.setAttribute("aria-describedby", hinweisId);
    feld.required = true;
    const knopf = el("button", "connector-connect", "Eintragen");
    knopf.type = "submit";
    zeile.append(feld, knopf);
    const hinweis = el(
      "p",
      "panel-karte-hinweis",
      "Für Tests mit einem im Panel ausgestellten Schlüssel. Er wird verschlüsselt gespeichert und hier nie wieder angezeigt.",
    );
    hinweis.id = hinweisId;
    form.append(label, zeile, hinweis);
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      const schluessel = feld.value.trim();
      if (!schluessel) {
        melde("Bitte den Schlüssel aus dem Panel eintragen.", "warn");
        feld.focus();
        return;
      }
      knopf.disabled = true;
      feld.disabled = true;
      melde("Verbinde mit dem Panel…");
      const rumpf = JSON.stringify({ schluessel });
      // Sofort weg aus der Seite — auch wenn das Verbinden scheitert.
      feld.value = "";
      void frage(PFAD, { method: "POST", headers: { "Content-Type": "application/json" }, body: rumpf })
        .then(({ daten }) => {
          if (daten?.ok && daten.verbunden) {
            zeige(daten);
            melde("Verbunden.", "ok");
            return;
          }
          melde(daten?.satz ?? "Das Verbinden hat nicht geklappt.", "warn");
          knopf.disabled = false;
          feld.disabled = false;
          feld.focus();
        })
        .catch(() => {
          melde("Keine Verbindung zum Server — bitte noch einmal versuchen.", "warn");
          knopf.disabled = false;
          feld.disabled = false;
        });
    });
    kasten.append(zusammenfassung, form);
    return kasten;
  };

  /** `davor`: weitere Knöpfe derselben Reihe — Aktionen stehen nebeneinander, nicht gestapelt (Design-Norm B4). */
  const trennKnopf = (umgestellt = false, davor: HTMLElement[] = []): HTMLElement => {
    const aktionen = el("div", "connector-actions panel-karte-aktionen");
    const knopf = el("button", "connector-disconnect", "Trennen");
    knopf.type = "button";
    knopf.setAttribute("aria-label", "Panel-Verbindung trennen");
    knopf.addEventListener("click", () => {
      // Martins Konto nach der Umstellung: ohne Verbindung gibt es KEINEN Zugang mehr (kein fester Schlüssel).
      // Der Name des angemeldeten Kontos, wenn die Seite ihn kennt — wer im falschen Konto landet, sieht es hier.
      const konto = angemeldetesKonto();
      const frageKopf = konto ? `Panel-Verbindung von „${konto}“ wirklich trennen?` : "Panel-Verbindung wirklich trennen?";
      const bestaetigung = umgestellt
        ? `${frageKopf} ${SAETZE_BETREIBER.trennen}`
        : `${frageKopf} Vespera sperrt dabei den Schlüssel im Panel und löscht ihn hier.`;
      if (!window.confirm(bestaetigung)) return;
      knopf.disabled = true;
      void frage(PFAD, { method: "DELETE" })
        .then(({ daten }) => {
          if (daten?.ok) {
            zeige({
              verbunden: false,
              darf_eintragen: daten.darf_eintragen,
              // Der Marker bleibt nach dem Trennen: Martins Karte sagt es.
              ...(daten.konto === "betreiber" ? { konto: "betreiber" as const, umgestellt: daten.umgestellt } : {}),
            });
            if (daten.panel === "stoerung") {
              melde("Hier getrennt. Das Panel war gerade nicht erreichbar – trenne die Verbindung bitte auch im Panel unter „Verbindungen“.", "warn");
            } else if (daten.panel === "nicht_gekoppelt") {
              melde("Hier getrennt. Diesen Schlüssel hat SSI im Panel von Hand ausgestellt – gesperrt wird er dort.");
            } else {
              melde("Getrennt.", "ok");
            }
            return;
          }
          knopf.disabled = false;
          melde("Das Trennen hat nicht geklappt.", "warn");
        })
        .catch(() => {
          knopf.disabled = false;
          melde("Keine Verbindung zum Server — bitte noch einmal versuchen.", "warn");
        });
    });
    aktionen.append(...davor, knopf);
    return aktionen;
  };

  /** Eine Zeile der Übersicht: Begriff und Wert. */
  const zeile = (begriff: string, wert: Node | string): HTMLElement => {
    const z = el("div");
    const dt = el("dt", undefined, begriff);
    const dd = el("dd");
    dd.append(wert);
    z.append(dt, dd);
    return z;
  };

  /** Martins Konto nach der Umstellung: kein Schlüssel von Hand, kein fester Schlüssel (Server: 409). */
  const zeige = (d: PanelAnsicht): void => {
    inhalt.textContent = "";
    const martin = d.konto === "betreiber";
    unter.textContent = panelStichworte(d, martin ? SAETZE_BETREIBER.unter : KUNDEN_UNTER);
    if (!d.verbunden) {
      const umgestellt = martin && d.umgestellt === true;
      status.textContent = "nicht verbunden";
      if (umgestellt) status.dataset.kind = "warn";
      else delete status.dataset.kind;
      inhalt.append(
        el(
          "p",
          "panel-karte-satz",
          !martin
            ? "Verbinde dein Konto im SSI-Panel, dann kannst du deine Webseite im Gespräch ansehen und ändern lassen – mit deinem eigenen Zugang."
            : umgestellt
              ? SAETZE_BETREIBER.getrennt
              : SAETZE_BETREIBER.fest,
        ),
        verbindenKnopf("Mit Panel verbinden"),
        el(
          "p",
          "panel-karte-hinweis",
          martin
            ? SAETZE_BETREIBER.festHinweis
            : "Du meldest dich dafür im Panel an und bestätigst dort, was Vespera darf. Den Schlüssel sieht niemand – er geht direkt vom Panel zu Vespera.",
        ),
      );
      if (d.darf_eintragen && !umgestellt) inhalt.append(betriebsFormular());
      return;
    }
    if (d.gilt === false) {
      status.textContent = "gilt nicht mehr";
      status.dataset.kind = "warn";
      inhalt.append(
        // Eigener Satz: der Hinweis aus /api (d.hinweis) ist für das Gespräch
        // geschrieben und schickte hier in die Einstellungen, in denen man steht.
        el(
          "p",
          "panel-karte-satz",
          martin ? SAETZE_BETREIBER.gilt : "Das Panel nimmt die Verbindung nicht mehr an. Verbinde neu oder trenne sie.",
        ),
        verbindenKnopf("Neu verbinden"),
        trennKnopf(martin && d.umgestellt === true),
      );
      if (d.darf_eintragen && !(martin && d.umgestellt === true)) inhalt.append(betriebsFormular());
      return;
    }
    // Nachlauf 28.09.2026: ist das Panel gerade gestört, sagt es der Kopf, und
    // der Satz des Servers steht VOR der Liste — er sagt, wie alt sie ist.
    const gestoert = d.erreichbar === false;
    /*
     * Auflage Prüfer 22.10.2026: die Karte beginnt zugeklappt — eine Warnung darf darum nicht nur im Körper stehen.
     * Läuft die Verbindung bald ab (danach kein Panel-Zugang) oder fehlt Martin das Buchhaltungsrecht, sagt es die
     * Marke in Worten (nicht nur Farbe), und die Karte steht offen, damit der Satz zu sehen ist — ohne es zu merken.
     */
    const ablaufBald = martin && d.ablauf_bald === true;
    const bereichIds = new Set((d.bereiche ?? []).map((b) => b.id));
    const ohneBuchhaltung = martin && !bereichIds.has("faktura") && !bereichIds.has("belege");
    status.textContent = gestoert ? "nicht erreichbar" : ablaufBald ? "läuft bald ab" : ohneBuchhaltung ? "unvollständig" : "verbunden";
    status.dataset.kind = gestoert || ablaufBald || ohneBuchhaltung ? "warn" : "ok";
    if (ablaufBald || ohneBuchhaltung) setzeOffen(true);
    const hinweis = d.hinweis ? el("p", "panel-karte-hinweis", d.hinweis) : null;
    const als = el("p", "panel-karte-als", "Verbunden als ");
    const name = d.name?.trim() ?? "";
    als.append(el("span", "panel-karte-name", name !== "" ? name : "Panel-Benutzer"));
    // Martins Liste trägt längere Begriffe („Rechte · Newsletter“): eine breitere Begriffsspalte.
    const liste = el("dl", martin ? "panel-karte-liste panel-karte-liste-breit" : "panel-karte-liste");
    const module = d.module ?? [];
    if (module.length > 0) {
      const marken = el("ul", "panel-karte-marken");
      for (const m of module) {
        const li = el("li", undefined, m.name);
        if (m.gueltig_bis) li.append(el("span", "panel-karte-bis", ` · bis ${panelDatum(m.gueltig_bis)}`));
        marken.append(li);
      }
      liste.append(zeile("Freigeschaltet", marken));
    } else {
      liste.append(zeile("Freigeschaltet", "noch nichts"));
    }
    const seiten = (d.webseiten ?? []).map((w) => w.domain);
    liste.append(zeile(seiten.length === 1 ? "Webseite" : "Webseiten", seiten.length ? seiten.join(", ") : "keine"));
    if (martin) {
      // Wann umgestellt wurde — ab dann gibt es keine festen Schlüssel mehr.
      if (d.umgestellt_seit) liste.append(zeile("Umgestellt seit", panelDatum(d.umgestellt_seit)));
      // Je Bereich die erteilten Rechte, in Worten (nie die technischen Namen).
      for (const b of d.bereiche ?? []) {
        const rechte = el("ul", "panel-karte-marken panel-karte-rechte");
        for (const r of b.rechte) {
          const li = el("li", undefined, r.text);
          if (r.riskant) li.dataset.riskant = "1";
          rechte.append(li);
        }
        liste.append(zeile(`Rechte · ${b.titel}`, rechte));
      }
      // „Nicht erteilt“ als Text, nicht nur als fehlende Marke — auch ohne Farbe lesbar.
      const nicht = (d.nicht_erteilt ?? []).map((r) => r.text);
      if ((d.bereiche ?? []).length > 0 || nicht.length > 0) {
        liste.append(zeile("Nicht erteilt", nicht.length > 0 ? nicht.join(", ") : "nichts – alles erteilt"));
      }
    }
    liste.append(zeile("Gültig bis", panelDatum(d.gueltig_bis)));
    inhalt.append(als);
    if (hinweis && gestoert) inhalt.append(hinweis);
    if (ablaufBald) {
      const warnung = el("p", "connector-warn panel-karte-warnung", satzAblauf(d.gueltig_bis));
      warnung.setAttribute("role", "status");
      inhalt.append(warnung);
    }
    if (ohneBuchhaltung) {
      const warnung = el("p", "connector-warn panel-karte-warnung", SAETZE_BETREIBER.keinBuchhaltungsrecht);
      warnung.setAttribute("role", "status");
      inhalt.append(warnung);
    }
    inhalt.append(liste);
    if (hinweis && !gestoert) inhalt.append(hinweis);
    if (martin) {
      inhalt.append(el("p", "panel-karte-hinweis", SAETZE_BETREIBER.rechteAendern));
      const lesen = el("button", "connector-connect", "Rechte neu lesen");
      lesen.type = "button";
      lesen.addEventListener("click", () => {
        lesen.disabled = true;
        lesen.setAttribute("aria-busy", "true");
        melde("Vespera fragt das Panel …");
        void laden(true).then((ok) => {
          if (ok) melde("Rechte neu gelesen.", "ok");
        });
      });
      /*
       * Nachbesserung 29.09.2026: auch im verbundenen Zustand „Neu verbinden“. Die Sätze
       * der Karte schicken dorthin (fehlendes Buchhaltungsrecht, Ablaufwarnung, „nicht
       * erteilt“) — ohne den Knopf blieb nur Trennen, und danach gibt es für Martin
       * keinen Panel-Zugang mehr (kein fester Schlüssel): ein riskanter Umweg genau im
       * Weg zurück. Neu verbinden ersetzt die Verbindung erst, wenn das Panel bestätigt hat.
       */
      inhalt.append(trennKnopf(d.umgestellt === true, [lesen, kopplungsKnopf("Neu verbinden")]));
      return;
    }
    inhalt.append(trennKnopf());
  };

  let letzte: PanelAnsicht | null = null;
  /** `frisch`: „Rechte neu lesen“ — der Server ignoriert die zehn Minuten und fragt das Panel. */
  const laden = (frisch = false): Promise<boolean> =>
    frage(frisch ? `${PFAD}?frisch=1` : PFAD)
      .then(({ status: code, daten }) => {
        if (daten?.ok) {
          letzte = daten;
          zeige(daten);
          return true;
        }
        status.textContent = code === 503 ? "gerade nicht bereit" : "Fehler";
        status.dataset.kind = "warn";
        melde("Die Panel-Verbindung ließ sich nicht laden.", "warn");
        return false;
      })
      .catch(() => {
        status.textContent = "Fehler";
        status.dataset.kind = "warn";
        melde("Keine Verbindung zum Server.", "warn");
        return false;
      });

  // Die Rückkehr aus dem Panel (Ticket 2713 V2): was dort herauskam, steht hier.
  const ergebnis = nimmKopplungsErgebnis();
  const kontoName = nimmKopplungsKonto();
  void laden().then(() => {
    const m = ergebnis ? kopplungsMeldung(ergebnis, kontoName) : null;
    if (!m) return;
    // Zurück aus dem Panel: die Karte steht offen — man will sehen, was jetzt gilt.
    setzeOffen(true);
    // Martin: nach dem Verbinden läuft ab jetzt alles über diese Verbindung.
    const zusatz = ergebnis === "verbunden" && letzte?.konto === "betreiber" ? ` ${SAETZE_BETREIBER.verbundenDanach}` : "";
    melde(`${m.text}${zusatz}`, m.art);
  });

  return karte;
}
